import type { Vec3 } from './mathx';

export interface RadarDetectionConfig {
  /** Probability of detection at rangeRef for a target with rcsRef. */
  pdRef: number;
  /** Reference slant range, metres. */
  rangeRef: number;
  /** Reference radar cross-section, square metres. */
  rcsRef: number;
  /** Minimum normalized SNR accepted by the receiver. */
  minSnr: number;
}

export interface RadarConfig {
  mountEnu: Vec3;
  /** Radar boresight yaw in ENU radians; zero points east. */
  mountYaw: number;
  /** Azimuth sector relative to boresight. */
  azSector: { centerDeg: number; widthDeg: number };
  elLimits: { minDeg: number; maxDeg: number };
  rangeMin: number;
  rangeMax: number;
  scanPeriod: number;
  rangeSigma: number;
  /** Azimuth measurement standard deviation, radians. */
  azSigma: number;
  /** Elevation measurement standard deviation, radians. */
  elSigma: number;
  /** Range-rate measurement standard deviation, m/s. Defaults to zero. */
  rangeRateSigma?: number;
  detection: RadarDetectionConfig;
  /** Poisson false-alarm mean per completed scan. */
  farPerScan: number;
  /** Defaults to true; setting it false gives clutter-free runs. */
  falseAlarmsEnabled?: boolean;
}

export const DEFAULT_RADAR_FAR_PER_SCAN = 0.05;

export class RadarConfigError extends Error {
  constructor(
    readonly path: string,
    message: string,
  ) {
    super(`radar config: ${path} ${message}`);
    this.name = 'RadarConfigError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const objectAt = (value: unknown, path: string): Record<string, unknown> => {
  if (!isRecord(value)) throw new RadarConfigError(path, 'must be an object');
  return value;
};

const finiteNumber = (value: unknown, path: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new RadarConfigError(path, 'must be a finite number');
  }
  return value;
};

const positiveNumber = (value: unknown, path: string): number => {
  const parsed = finiteNumber(value, path);
  if (parsed <= 0) throw new RadarConfigError(path, 'must be > 0');
  return parsed;
};

const nonNegativeNumber = (value: unknown, path: string): number => {
  const parsed = finiteNumber(value, path);
  if (parsed < 0) throw new RadarConfigError(path, 'must be >= 0');
  return parsed;
};

const vec3At = (value: unknown, path: string): Vec3 => {
  const raw = objectAt(value, path);
  return {
    x: finiteNumber(raw.x, `${path}.x`),
    y: finiteNumber(raw.y, `${path}.y`),
    z: finiteNumber(raw.z, `${path}.z`),
  };
};

/** Validate and defensively copy radar configuration loaded from JSON/scenario data. */
export const parseRadarConfig = (value: unknown): RadarConfig => {
  const raw = objectAt(value, 'root');
  const azSectorRaw = objectAt(raw.azSector, 'azSector');
  const elLimitsRaw = objectAt(raw.elLimits, 'elLimits');
  const detectionRaw = objectAt(raw.detection, 'detection');

  const azSector = {
    centerDeg: finiteNumber(azSectorRaw.centerDeg, 'azSector.centerDeg'),
    widthDeg: positiveNumber(azSectorRaw.widthDeg, 'azSector.widthDeg'),
  };
  if (azSector.widthDeg > 360) {
    throw new RadarConfigError('azSector.widthDeg', 'must be <= 360');
  }

  const elLimits = {
    minDeg: finiteNumber(elLimitsRaw.minDeg, 'elLimits.minDeg'),
    maxDeg: finiteNumber(elLimitsRaw.maxDeg, 'elLimits.maxDeg'),
  };
  if (elLimits.minDeg < -90 || elLimits.minDeg > 90) {
    throw new RadarConfigError('elLimits.minDeg', 'must be between -90 and 90');
  }
  if (elLimits.maxDeg < -90 || elLimits.maxDeg > 90) {
    throw new RadarConfigError('elLimits.maxDeg', 'must be between -90 and 90');
  }
  if (elLimits.maxDeg <= elLimits.minDeg) {
    throw new RadarConfigError('elLimits.maxDeg', 'must be greater than elLimits.minDeg');
  }

  const rangeMin = nonNegativeNumber(raw.rangeMin, 'rangeMin');
  const rangeMax = positiveNumber(raw.rangeMax, 'rangeMax');
  if (rangeMax <= rangeMin) {
    throw new RadarConfigError('rangeMax', 'must be greater than rangeMin');
  }

  const pdRef = finiteNumber(detectionRaw.pdRef, 'detection.pdRef');
  if (pdRef < 0 || pdRef > 1) {
    throw new RadarConfigError('detection.pdRef', 'must be between 0 and 1');
  }
  const minSnr = nonNegativeNumber(detectionRaw.minSnr, 'detection.minSnr');
  if (minSnr >= 1) {
    throw new RadarConfigError(
      'detection.minSnr',
      'must be < 1 because reference-target SNR is normalized to 1',
    );
  }

  const falseAlarmsEnabled = raw.falseAlarmsEnabled;
  if (falseAlarmsEnabled !== undefined && typeof falseAlarmsEnabled !== 'boolean') {
    throw new RadarConfigError('falseAlarmsEnabled', 'must be a boolean');
  }

  return {
    mountEnu: vec3At(raw.mountEnu, 'mountEnu'),
    mountYaw: finiteNumber(raw.mountYaw, 'mountYaw'),
    azSector,
    elLimits,
    rangeMin,
    rangeMax,
    scanPeriod: positiveNumber(raw.scanPeriod, 'scanPeriod'),
    rangeSigma: nonNegativeNumber(raw.rangeSigma, 'rangeSigma'),
    azSigma: nonNegativeNumber(raw.azSigma, 'azSigma'),
    elSigma: nonNegativeNumber(raw.elSigma, 'elSigma'),
    rangeRateSigma:
      raw.rangeRateSigma === undefined
        ? 0
        : nonNegativeNumber(raw.rangeRateSigma, 'rangeRateSigma'),
    detection: {
      pdRef,
      rangeRef: positiveNumber(detectionRaw.rangeRef, 'detection.rangeRef'),
      rcsRef: positiveNumber(detectionRaw.rcsRef, 'detection.rcsRef'),
      minSnr,
    },
    farPerScan:
      raw.farPerScan === undefined
        ? DEFAULT_RADAR_FAR_PER_SCAN
        : nonNegativeNumber(raw.farPerScan, 'farPerScan'),
    falseAlarmsEnabled: falseAlarmsEnabled ?? true,
  };
};
